import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import { SpaceviewerElementFormField } from "../../../ElementDialog";
import Spaceviewer, { SpaceviewerAppearance } from "rs-components/Spaceviewer";
import Parameters from "./components/Parameters";
import Sources from "./components/Sources";
import { setServerError } from "store/knowledge-base-item/knowledge-base-item.actions";
import { setSpaceviewerAxis, setSpaceviewerConfidence, setSpaceviewerDensity, setSpaceviewerSourceView, } from "store/rs-workspace/actions/results.actions";
import { getCalculationSpaceviewerResultSelector } from "store/rs-workspace/selectors/results.selectors";
import { ScrollElements } from "store/global/global.types";
import { ParameterFields, SourceFields } from "./types";
import { isNotEmpty } from "utils/validate";
import { getNextMaxId } from "utils/data";
import * as styles from "./styles.module.scss";
export const defaultParameter = {
    [ParameterFields.parameter]: null,
    [ParameterFields.min]: null,
    [ParameterFields.max]: null,
    [ParameterFields.granularity]: null,
};
export const defaultSource = {
    [SourceFields.density]: null,
    [SourceFields.colorProfile]: null,
    [SourceFields.view]: false,
    [SourceFields.source]: null,
};
const SpaceViewerElement = ({ form, resultSetId, elementId, elementData, isCompleted, restrictions, }) => {
    const dispatch = useDispatch();
    const { data, error, sourceVariable, sourceLabel, selectedAxisIndexes, isInsight } = useSelector(getCalculationSpaceviewerResultSelector({ resultSetId, resultSetElementId: elementId }));
    const editMode = useMemo(() => !!form, [form]);
    const upsert = useCallback((values, field) => {
        const currentItem = get(elementData, field);
        if (isNotEmpty(values.id)) {
            const index = currentItem.findIndex(({ id }) => id === values.id);
            if (index !== -1) {
                if (field === SpaceviewerElementFormField.sources &&
                    !!values[SourceFields.view]) {
                    currentItem.forEach((source, index) => {
                        const updatedSource = { ...source, [SourceFields.view]: false };
                        form?.mutators.update(field, index, updatedSource);
                    });
                }
                form?.mutators.update(field, index, values);
                dispatch(setServerError(null));
                return;
            }
        }
        if (field === SpaceviewerElementFormField.sources) {
            values[SourceFields.view] = currentItem.length === 0;
        }
        form?.mutators.push(field, {
            ...values,
            id: getNextMaxId(currentItem),
        });
    }, [dispatch, elementData, form?.mutators]);
    const remove = useCallback((id, field) => {
        const currentItem = [...get(elementData, field)];
        const index = currentItem.findIndex((item) => item.id === id);
        if (index !== -1) {
            currentItem.splice(index, 1);
        }
        if (field === SpaceviewerElementFormField.sources && isNotEmpty(currentItem[0])) {
            currentItem[0][SourceFields.view] = true;
        }
        form?.change(field, currentItem);
    }, [elementData, form]);
    const changeSelectedAxisIndexes = useCallback((indexes) => {
        if (indexes === null) {
            return;
        }
        const xAxisId = elementData.parameters[indexes[0]].id;
        const yAxisId = elementData.parameters[indexes[1]].id;
        if (resultSetId === null || xAxisId === undefined || yAxisId === undefined) {
            return;
        }
        dispatch(setSpaceviewerAxis({ resultSetId, elementId: elementId, xAxisId, yAxisId }));
    }, [dispatch, elementData.parameters, elementId, resultSetId]);
    const changeConfidence = useCallback((confidence) => {
        const currentSource = elementData.sources.find(({ view }) => view);
        if (currentSource && isNotEmpty(currentSource.id)) {
            dispatch(setSpaceviewerConfidence({
                resultSetId,
                elementId,
                sourceId: currentSource.id,
                ...confidence,
            }));
        }
    }, [dispatch, elementData.sources, elementId, resultSetId]);
    const handleChangeView = useCallback((id) => {
        if (id !== undefined) {
            dispatch(setSpaceviewerSourceView({ resultSetId, elementId, id }));
        }
    }, [dispatch, elementId, resultSetId]);
    const changeDensity = useCallback((density) => {
        const currentSource = elementData.sources.find(({ view }) => view);
        if (currentSource && isNotEmpty(currentSource.id)) {
            dispatch(setSpaceviewerDensity({
                resultSetId,
                elementId,
                sourceId: currentSource.id,
                density,
            }));
        }
    }, [dispatch, elementData.sources, elementId, resultSetId]);
    return (React.createElement("div", { className: styles.root },
        !editMode && (React.createElement("div", { className: classNames(styles.spaceviewer, {
                [styles["spaceviewer-d1"]]: data.parameters.length < 2,
            }) },
            React.createElement(Spaceviewer, { data: data, elementId: elementId, selectedAxisIndexes: selectedAxisIndexes, changeSelectedAxisIndexes: changeSelectedAxisIndexes, changeConfidence: changeConfidence, changeDensity: changeDensity, appearance: isInsight ? SpaceviewerAppearance.InsightResult : SpaceviewerAppearance.SimulationResult, error: error && isCompleted && data?.parameters.length > 1 ? error : "", legendData: { sourceVariable, sourceLabel }, scrollElement: ScrollElements.rsWorkspaceResultSet, spaceviewerUid: `${resultSetId}-${elementId}` }))),
        (editMode || elementData.showSettings) && (React.createElement("div", { className: styles.config },
            React.createElement(Parameters, { editMode: editMode, elementData: elementData, upsert: (values) => upsert(values, SpaceviewerElementFormField.parameters), remove: (id) => remove(id, SpaceviewerElementFormField.parameters), restrictions: restrictions }),
            React.createElement(Sources, { editMode: editMode, elementData: elementData, upsert: (values) => upsert(values, SpaceviewerElementFormField.sources), remove: (id) => remove(id, SpaceviewerElementFormField.sources), handleChangeView: handleChangeView, restrictions: restrictions, resultSetId: resultSetId })))));
};
export default SpaceViewerElement;
